import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { ALL_PRODUCTS } from "@/data/products";
import { useCatalog, isHiddenById } from "@/context/CatalogContext";
import { getRecentProductIds, removeProductFromRecent } from "@/lib/recentlyViewed";
import ProductCard from "@/components/store/ProductCard";

export default function RecentlyViewed({ excludeId, className = "" }) {
  const { t, isRTL } = useLanguage();
  const S = t.store;
  const [ids, setIds] = useState([]);
  const trackRef = useRef(null);
  const { version: catalogVersion } = useCatalog();

  useEffect(() => {
    setIds(getRecentProductIds());
  }, []);

  const items = useMemo(() => {
    const byId = new Map(ALL_PRODUCTS.map((p) => [p.id, p]));
    return ids
      .filter((id) => id !== excludeId && !isHiddenById(id))
      .map((id) => byId.get(id))
      .filter(Boolean);
  }, [ids, excludeId, catalogVersion]);

  if (items.length === 0) return null;

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.8 * (isRTL ? -dir : dir);
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  const handleRemove = (id) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    removeProductFromRecent(id);
    setIds((prev) => prev.filter((x) => x !== id));
  };

  return (
    <section
      className={`px-5 md:px-8 pt-10 md:pt-14 ${className}`}
      data-testid="recently-viewed-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="mb-5 sm:mb-7 flex items-end justify-between gap-4">
          <div>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.3em] uppercase text-[#F37021] mb-2">
              <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
              {S.recentlyViewedEyebrow}
            </span>
            <h2
              className="font-display text-2xl font-bold leading-tight text-[#111] sm:text-3xl dark:text-white"
              data-testid="recently-viewed-title"
            >
              {S.recentlyViewedTitle}
            </h2>
          </div>
          {items.length > 2 && (
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                aria-label={S.recentlyViewedPrev}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-[#111] transition-colors hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                data-testid="recently-viewed-prev"
              >
                {isRTL ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                aria-label={S.recentlyViewedNext}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-white text-[#111] transition-colors hover:border-[#F37021] hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                data-testid="recently-viewed-next"
              >
                {isRTL ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>

        <div
          ref={trackRef}
          className="flex gap-4 md:gap-6 overflow-x-auto pb-3 -mx-5 md:-mx-8 px-5 md:px-8 scroll-smooth snap-x snap-mandatory recently-viewed-track"
          data-testid="recently-viewed-track"
        >
          {items.map((p) => (
            <div
              key={p.id}
              className="relative shrink-0 w-[58vw] sm:w-[260px] md:w-[230px] lg:w-[240px] snap-start"
              data-testid={`recently-viewed-item-${p.id}`}
            >
              <button
                type="button"
                onClick={handleRemove(p.id)}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
                aria-label={S.recentlyViewedRemove}
                title={S.recentlyViewedRemove}
                className="absolute -top-2 end-1.5 z-20 inline-flex items-center justify-center w-8 h-8 rounded-full bg-black/70 backdrop-blur text-white hover:bg-[#F37021] focus:bg-[#F37021] focus:outline-none focus:ring-2 focus:ring-[#F37021]/40 transition-colors shadow-[0_4px_12px_rgba(0,0,0,0.25)]"
                data-testid={`recently-viewed-remove-${p.id}`}
              >
                <X className="w-4 h-4" strokeWidth={2.5} />
              </button>
              <ProductCard product={p} source="recent" />
            </div>
          ))}
        </div>
      </div>

      {/* Hide scrollbar visuals — keep it premium but still scrollable */}
      <style>{`
        .recently-viewed-track {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .recently-viewed-track::-webkit-scrollbar { display: none; }
      `}</style>
    </section>
  );
}
